import { FileText } from 'lucide-react';
import { useSend } from './SendContext';
import { useT } from '../I18nContext';

/**
 * Assistant-side thinking bubble. Three pulsing dots by default.
 *
 * When the optimistic source preview lands (~500ms after send,
 * via /api/chat/preview), upgrades to:
 *
 *    [Lᴀ] Found an answer in foo.docx, bar.pdf · Generating a response…
 *
 * That gives users meaningful feedback while the actual Gemini
 * round-trip continues. As soon as the real ask settles, the
 * indicator goes away and the assistant's full bubble takes over
 * (with the same sources rendered as MessageSources).
 *
 * Pure visual - no state of its own beyond reading SendContext.
 * The MessageStream decides when to mount it via SendContext.isPending.
 */
export function ThinkingIndicator() {
  const { previewedSources } = useSend();
  const t = useT();

  // Trim to 3 sources to match what the assistant turn will show
  // (avoids an "8 sources here, 3 sources after" surprise).
  const visible = (previewedSources ?? []).slice(0, 3);
  const hasSources = visible.length > 0;

  return (
    <div className="message-row is-assistant">
      <div className="message-avatar message-avatar-assistant" aria-hidden>
        L
      </div>
      <div
        className={
          'message-bubble' +
          (hasSources ? ' message-bubble-thinking-rich' : ' message-bubble-thinking')
        }
        aria-live="polite"
      >
        {hasSources ? (
          <div className="thinking-rich">
            <div className="thinking-rich-line">
              <FileText size={12} className="thinking-rich-icon" aria-hidden />
              <span>
                <strong>{t('thinking.found_in')}</strong>
                {visible.map((s, i) => (
                  <span key={s.id || i}>
                    {i > 0 && <span className="thinking-rich-sep">, </span>}
                    {s.title || t('message.source_doc_default')}
                  </span>
                ))}
              </span>
            </div>
            <div className="thinking-rich-status">
              <span className="thinking-dot" />
              <span className="thinking-dot" />
              <span className="thinking-dot" />
              <span>{t('thinking.generating')}</span>
            </div>
          </div>
        ) : (
          <>
            <span className="thinking-dot" />
            <span className="thinking-dot" />
            <span className="thinking-dot" />
            <span className="visually-hidden">{t('thinking.label')}</span>
          </>
        )}
      </div>
    </div>
  );
}
